<template>
  <div
    class="tender-info-panel"
    :class="{'is-open': currentBlock.component, 'has-scroll': hasScroll}"
    :data-qa="dataQa"
    :style="`--panel-width: ${panelWidth}px; --right-block-compact: 44px;`">
    <div class="tender-page-left">
      <div class="tender-page-header">
        <Breadcrumbs
          class="pa-0"
          :items="breadcrumbs"/>
        <ItemTopActions
          :options="{
            id: application.id,
            number: application.number,
            title: application.title,
            shareLabel: $t('Purchasing.shareLabel'),
            routeLinkOrg: 'purchitem'
          }"/>
      </div>
      <div class="pt-2 width-100">
        <template v-if="loading">
          <v-skeleton-loader
            class="mt-10 pr-8"
            type="heading"
            width="100%"/>
          <v-skeleton-loader
            class="mt-3 pr-8"
            type="sentences"
            width="100%"/>
          <v-skeleton-loader
            class="mt-4 main__green mt-5"
            type="table-thead, table-tbody"
            width="100%"/>
        </template>
        <template v-else-if="application">
          <TemplateItemTitle
            :item="application"
            :loading="loading">
            <PurchaseAgreBlock
              :can-agree.sync="canAgree"
              :elements="selected"
              :item="application"
              :loading="loading"
              @getAcceptors="getAcceptors"/>
          </TemplateItemTitle>
          <PurchaseItemBody
            :acceptors="acceptors"
            class="pt-5"
            :item.sync="application"
            :loading="loading"/>
        </template>
        <template v-else>
          <sAlert
            class="mt-5"
            color="error"
            data-qa="purchase-item-error"
            text>
            {{ $t('Common.ErrorTitle') }}
          </sAlert>
        </template>
      </div>
    </div>
    <div
      v-show="currentBlock && currentBlock.component"
      id="seperator"/>
    <div
      class="tender-info-panel__right">
      <div
        class="tender-info-panel__right-menu">
        <div
          v-if="currentBlock.component"
          class="tender-info-panel__right-title">
          <div/>
          <span
            class="tender-info-panel__right-title-label"
            data-qa="history-dialog-name">{{ currentBlock.name }}</span>
        </div>
        <div
          class="my-auto">
          <v-menu
            v-if="!loading"
            content-class="purch-card-options-menu menu"
            left
            offset-x
            offset-y>
            <template #activator="{ on }">
              <v-btn
                data-qa="tender-info-menu-btn"
                :disabled="loading || !application"
                icon
                small
                v-on="on">
                <sIcon
                  color="var(--black-color)"
                  data-qa="menu-item"
                  size="22"
                  type="menu"/>
              </v-btn>
            </template>
            <div
              class="purch-card-menu menu"
              dense>
              <CopyItem
                :copy-item="$_copyApp"
                list/>
              <ToggleTemplates
                :add="!application || !application.inTemplates"
                list
                @click="toggleTemplate"/>
              <InDevelopmentInfo
                :condition="true"
                prevent>
                <template #activator>
                  <MenuItem
                    data-qa="check-chat"
                    disabled
                    icon="comment"
                    :label="$t('Common.JoinChat')"/>
                </template>
              </InDevelopmentInfo>
            </div>
          </v-menu>
        </div>
      </div>
      <div
        class="tender-info-panel__right-content">
        <div
          v-if="currentBlock.component"
          class="tender-info-panel__right-component"
          :data-qa="currentBlock.component">
          <component
            :is="currentBlock.component"
            v-bind="componentOptions"
            v-on="componentOn">
            <template #acceptors>
              <ListOfAcceptors
                :acceptors.sync="acceptors"
                class="mb-5"
                :item.sync="application"
                :with_loading="load_acceptors"
                @updateAgreements="getAcceptors"/>
            </template>
          </component>
        </div>
        <div
          class="tender-info-panel__right-btns">
          <sBadge
            v-for="listItem in listItems"
            :key="listItem.component"
            color="red"
            :content="listItem.count"
            :data-qa="`badge-${listItem.tabIcon}`"
            :dot="listItem.badge.dot"
            :inner-styles="listItem.badge.dot ? '' : 'padding: 2px; height: 14px; min-width: 14px;'"
            :offset-x="listItem.badge.offsetX"
            :offset-y="listItem.badge.offsetY"
            overlap
            :value="!!listItem.count">
            <TooltipedBtn
              class="tender-info-panel__right-item"
              :color="currentBlock.component === listItem.component ? 'var(--v-secondary__green_not_active-base)' : 'var(--text-color)'"
              :data-qa="`btn-${listItem.tabIcon}`"
              :disabled="loading || listItem.disabled || !application"
              :icon="listItem.tabIcon"
              icon_size="22px"
              :text="listItem.name"
              @click="changeItem(listItem.index)"/>
          </sBadge>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import TooltipedBtn from 'components/common/elements/TooltipedBtn.vue'
import TemplateItemTitle from 'components/templates/TemplateItemTitle.vue'
import PurchaseAgreBlock from 'components/purchasing/purch item/block/PurchaseAgreBlock.vue'

import ListOfAcceptors from 'components/agreement/ListOfAcceptors.vue'
import PurchaseInfo from 'components/purchasing/purch item/block/PurchaseInfo.vue'
import PurchaseHistory from 'components/purchasing/purch item/block/PurchaseHistory.vue'
import InDevelopmentInfo from 'components/common/elements/InDevelopmentInfo.vue'
import Breadcrumbs from 'components/common/elements/Breadcrumbs.vue'
import PurchaseItemBody from 'components/purchasing/purch item/PurchaseItemBody.vue'
import ItemTopActions from 'components/common/ItemTopActions.vue'

import MenuItem from 'components/common/MenuItem.vue'
import CopyItem from 'components/common/elements/CopyItem.vue'
import ToggleTemplates from 'components/common/elements/ToggleTemplates.vue'

import storageMixin from 'mixins/storageMixin.js'
import displaySize from 'mixins/display-size'

import { applicationMixin } from 'mixins'
import { mapGetters } from 'vuex'

import ApplicationsApi from '@/api/applicationApi'
const api = new ApplicationsApi()
import SmarttenderApi from '@/api/smarttenderApi'
const tender_api = new SmarttenderApi()

import { eventBus } from '@/main'

export default {
  name: 'PurchaseItem',
  mixins: [ displaySize, storageMixin, applicationMixin ],
  data() {
    return {
      itemId: null,
      purchaseInfoTab: 0,
      panelWidth: 356,
      currentBlock: {},
      selected: [],

      loading: true,
      load_acceptors: true,

      history: [],

      dataQa: 'purchase-item',
      application: {},
      acceptors: [],
      canAgree: false,
    }
  },
  methods: {
    async getAcceptors() {
      await api.getApplicationAcceptors(+this.itemId)
        .then(res => {
          this.application.acceptors = res
          this.acceptors = res
          this.canAgree = res?.some(el => el.status == 'Waiting' && el.user?.id?.trim() == this.user_id)
        })
        .catch(this.$log_error)
        .finally(() => this.load_acceptors = false)
    },
    async getApps() {
      return await api.getApplicationById(this.itemId)
        .then(this.getApplicationCallback)
        .catch(res => this.$log_error(res))
        .finally(() => {
          this.loading = false
        })
    },
    async getApplicationCallback(responce) {
      if (!responce.data?.applications) {
        this.$router.push({name: 'page-error', params: { status_code: responce.status || 500, descript: responce.data?.message || 'PurchaseItem emty'}})
        return
      }
      this.application = responce.data?.applications?.[0]
      if ( this.application?.state == 'П' ) {
        return this.$router.push( {name: 'editing', query: {id: this.application?.id}, params: {org: this.$route.params.org} })
      }
      if ( !this.application ) {
        this.$router.push({name: 'page-error', params: { status_code: 404, descript: 'ItemComponent'}})
      }
      this.loading = false

      this.getTenderStatuses()

      this.getAcceptors()
      await api.getApplicationEvents(+this.itemId)
        .then(res => {
          this.history = res.data.changeHistory
        })
    },
    getTenderStatuses() {
      // let t = this
      let rows = this.application.rows.filter(r => r.bindedTenders?.length > 0)
      let bindedTenders = rows.map(r => r.bindedTenders.find(el => !el.cancelled && el?.tenderId ) ||  r.bindedTenders[0] )
      let tenderIds = bindedTenders.map(r => r?.tenderId )
      tenderIds = [...new Set(tenderIds)]
      if (!tenderIds.length || tenderIds.some(el => !el)) {
        return ''
      }
      let promices = tenderIds.map(id => tender_api.getTenderNameStatus(id)
        .then(resp => {
          rows.find(row => row.bindedTenders.find(el => !el.cancelled && el?.tenderId == resp.tenderId )
            || row.bindedTenders.find(el => el?.tenderId == resp.tenderId )).tender = resp
          // t.$set(t.tenderStatuses, id, {tenderStatusTitle: resp?.title, tenderStatusId: resp?.statusCode})
        }))

      return Promise.all(promices)
    },
    async dragElement() {
      const element = document.getElementById('seperator')
      if (element) {
        element.onmousedown = this.dragMouseDown
      }
    },
    async changeItem(value, init) {
      this.purchaseInfoTab = value
      const componentProps = this.listItems.find(i => i.index === value)
      this.currentBlock = !init && this.currentBlock?.component === componentProps?.component ? {} : componentProps

      this.$emit('update:mini', !this.currentBlock?.component)
      await this.$_savePropToDBStorage('purchNavbar', this.currentBlock)

      await this.dragElement()
      /*if (!this.tender?.tenderId) {
        return
      }
      this.getPanelDate()*/
    },
    checkPanelWidth(panelWidth) {
      if (panelWidth + 100 > window.innerWidth) {
        return window.innerWidth - 100
      }
      return panelWidth
    },
    onMouseMove(e) {
      const distanceFromRight = window.innerWidth - e.clientX
      if (distanceFromRight <= 356) {
        return
      }
      this.$_savePropToDBStorage('purchNavbarWidth', distanceFromRight)
      this.panelWidth = this.checkPanelWidth(distanceFromRight)
    },
    dragMouseDown() {
      document.onmousemove = this.onMouseMove
      document.onmouseup = () => { document.onmousemove = document.onmouseup = null }
    },
  },
  computed: {
    ...mapGetters('notification', ['isActiveSignalR']),
    componentOptions() {
      switch (this.currentBlock?.component) {
      case 'PurchaseInfo':
        return {
          ref: 'info',
          application: this.application || {},
        }
      case 'PurchaseHistory':
        return {
          ref: 'info',
          application: this.application || {},
          history: this.history || [],
        }
      default:
        return {}
      }
    },
    componentOn() {
      switch (this.currentBlock?.component) {
      default:
        return {}
      }
    },
    breadcrumbs() {
      return [
        {
          index: 0,
          text: this.$t(`Menu.${this.mode[0].toUpperCase() + this.mode.slice(1)}`),
          maxLength: 100,
          to: { name: this.mode, params: {org: this.$route.params.org}}
        },
        {
          index: 1,
          maxLength: 20,
          text: '#' + (this.application?.number || ''),
          loading: this.loading
        },
      ]
    },
    listItems() {
      return [
        {
          tabIcon: 'info',
          name: 'Про замовлення',
          component: 'PurchaseInfo',
          badge: {},
          index: -1
        },
        {
          tabIcon: 'comment',
          name: 'Чат',
          badge: {},
          disabled: true,
          index: 1
        },
        {
          tabIcon: 'time-ending',
          name: this.$t('Tenders.History'),
          component: 'PurchaseHistory',
          badge: {},
          index: 2
        },
      //...this.$_tabs.map((i, index) => { return {...i, index: index } })
      ]
    },
  },
  created() {
    this.itemId = this.$route.params?.id
    this.$root.$on('updateEvents', this.updateEvents)

    if (!this.isActiveSignalR) {
      eventBus.$emit('ShowSignalAlert', 'Отримання закупки')
    } else {
      this.$notifier.onPage(this.itemId?.toString(), 1)
    }
    this.getApps()
  },
  mounted() {
    this.loading = true
    if (!this.breakpoint.smAndDown) {
      this.$_getPropFromDBStorage('purchNavbarWidth', 356)
        .then(async(result) => {
          this.panelWidth = this.checkPanelWidth(+result)
        })
      this.$_getPropFromDBStorage('purchNavbar', {})
        .then(async (result) => {
          try {
            const newIndex = JSON.parse(result)?.index
            if (result && newIndex !== undefined) {
              await this.changeItem(newIndex, true)
            } else {
              this.currentBlock = {}
              this.$emit('update:mini', true)
            }
          } catch (_) {
            /* */
          }
        })
    }
  },
  components: {
    MenuItem,
    CopyItem,
    TooltipedBtn,
    PurchaseInfo,
    PurchaseHistory,
    ListOfAcceptors,
    ToggleTemplates,
    PurchaseItemBody,
    TemplateItemTitle,
    InDevelopmentInfo,
    PurchaseAgreBlock,
    ItemTopActions,

    Breadcrumbs,
  },
  props: {
    mode: String,
  },
}
</script>
<style scoped lang="scss">
$class: '.tender-info-panel';

#{$class} {
  width: 100%;
  height: 100%;
  padding: 0px;
  //background-color: var(--v-main__light_green-base);
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 42px;
  height: 100%;
  transition: grid-template-columns 0.3s ease-in-out;
  &>*:first-child>* {
    padding-right: 5px;
    min-width: calc(100% - var(--right-block-compact));
    max-width: calc(100vw - 55px);
    transition: min-width 0.3s ease-in-out;
  }
  &__content {
    background-color: var(--v-main__light_green-base) !important;
  }
  &__right {
    z-index: 10;
    position: fixed;
    top: 0;
    right: 0;
    height: 100%;
    display: grid;
    align-items: center;
    justify-items: center;
    grid-template-columns: 1fr;
    grid-template-rows: var(--right-block-compact) 1fr;
    background-color: var(--v-main__light_green-base) !important;
    width: var(--right-block-compact);
    transition: width 0.3s ease-in-out;
    &.is-footer {
      grid-template-rows: var(--right-block-compact) 1fr 60px;
    }
    &-content {
      margin: 0 0 auto 0;
      height: 100%;
    }
    &-title {
      display: grid;
      grid-template-columns: 40px auto;
      &-label {
        font-size: 24px;
        font-weight: 600;
        line-height: 32px;
      }
    }
    &-menu {
      border-bottom: 1px solid var(--main__green_locked);
      height: 100%;
    }
    &-item {
      cursor: pointer;
      transition: 0.25s all;
    }
    &-menu,
    &-content {
      display: grid;
      grid-template-columns: var(--right-block-compact);
      width: 100%;
    }
    &-btns {
      padding-top: 10px;
      gap: 10px;
      display: grid;
      align-items: center;
      justify-items: center;
      grid-template-rows: repeat(auto-fill, 41px);
      height: 100%;
      border-left: 1px solid transparent;
    }
  }
}
#{$class}.is-open {
  grid-template-columns: calc(100% - 5px - var(--panel-width)) 5px var(--panel-width);
  transition: grid-template-columns 0.3s ease-in-out;
  &.has-scroll {
    grid-template-columns: calc(100% - 13px - var(--panel-width)) 5px var(--panel-width);
  }
  &>*:first-child>* {
    min-width: calc(100% - var(--panel-width));
    transition: min-width 0.3s ease-in-out;
  }
  #{$class}__right {
    width: var(--panel-width);
    transition: width 0.3s ease-in-out;
    &-menu,
    &-content {
      grid-template-columns: minmax(50px, 1fr) var(--right-block-compact);
      transition: grid-template-columns 0.3s ease-in-out;
    }
    &-btns {
      border-left: 1px solid var(--main__green_locked);
    }
    &-component {
      max-height: calc(100vh - var(--right-block-compact));
    }
  }
}
.tender-page-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--v-main__green-base);
  //max-width: calc(100vw - 55px);
  height: 44px;
  display: grid;
  padding: 0 !important;
  grid-template-columns: auto auto;
  border-bottom: 1px solid var(--main__green_locked);
  &::after {
    content: "";
    border-bottom: 1px solid var(--main__green_locked);
    position: absolute;
    right: -17px;
    bottom: -1px;
    width: 18px;
    height: 2px;
  }
}

.tender-page-left {
  max-width: 100vw;
  padding-left: 20px;
  padding-bottom: 16px;
  padding-right: 12px;
  position: relative;
}
</style>
